!function() {
  "use strict";

  // ================================
  // NEW: 24 Hours Frequency Manager
  // ================================
  class PopupFrequencyManager {
    constructor() {
      this.storageKeyPrefix = 'popup_closed_24h_'; // Unique key per popup
      this.hours = 24;
    }

    // Check if popup should be shown (not closed in last 24 hours)
    shouldShow(popupId) {
      if (!popupId) return true;
      
      const closedTime = localStorage.getItem(this.storageKeyPrefix + popupId);
      if (!closedTime) return true;

      const now = Date.now();
      const timeLimit = this.hours * 60 * 60 * 1000; // 24 hours in milliseconds

      return (now - parseInt(closedTime)) > timeLimit;
    }

    // Mark popup as closed now
    markAsClosed(popupId) {
      if (!popupId) return;
      localStorage.setItem(this.storageKeyPrefix + popupId, Date.now().toString());
    }

    // Optional: Clear for testing
    clear(popupId) {
      if (!popupId) return;
      localStorage.removeItem(this.storageKeyPrefix + popupId);
    }
  }

  // Global instance
  window.popupFrequencyManager = new PopupFrequencyManager();

  // ================================
  // Existing Cookie Class (we keep it but won't use for frequency)
  // ================================
  class CookieManager {
    constructor(name, value) {
      this.configuration = {
        expires: null,
        path: "/",
        domain: window.location.hostname,
        sameSite: "none",
        secure: true
      };
      this.name = name;
      this.value = value;
    }
    write() {
      if (-1 !== document.cookie.indexOf("; ") && !document.cookie.split("; ").find((t => t.startsWith(this.name))) || -1 === document.cookie.indexOf("; ")) {
        document.cookie = `${this.name}=${this.value}; expires=${this.configuration.expires}; path=${this.configuration.path}; domain=${this.configuration.domain}; sameSite=${this.configuration.sameSite}; secure=${this.configuration.secure}`;
      }
    }
    read() {
      if (-1 !== document.cookie.indexOf("; ") && document.cookie.split("; ").find((t => t.startsWith(this.name)))) {
        return document.cookie.split("; ").find((t => t.startsWith(this.name))).split("=")[1];
      }
      return false;
    }
    destroy() {
      if (document.cookie.split("; ").find((t => t.startsWith(this.name)))) {
        document.cookie = `${this.name}=null; expires=${this.configuration.expires}; path=${this.configuration.path}; domain=${this.configuration.domain}; sameSite=${this.configuration.sameSite}; secure=${this.configuration.secure}`;
      }
    }
  }

  let openPopupsCount = 0;
  let scrollUnlockTimeout = 0;
  let activePopups = [];

  class PopupTrigger {
    constructor(container, popup) {
      this.popupContainer = container;
      this.popup = popup;
      this.popupBody = popup.querySelector("[data-popup-body]");
      this.delay = container.dataset.popupDelay;
      this.isSubmitted = -1 !== window.location.href.indexOf("accepts_marketing") || -1 !== window.location.href.indexOf("customer_posted=true");
      this.a11y = window.theme.a11y;
      this.showOnScrollHandler = () => this.showOnScroll();

      if ("always" === this.delay || this.isSubmitted) {
        this.always();
      }

      if (this.delay && this.delay.includes("delayed") && !this.isSubmitted) {
        const seconds = this.delay.includes("_") ? parseInt(this.delay.split("_")[1]) : 10;
        this.delayed(seconds);
      }

      if ("bottom" === this.delay && !this.isSubmitted) {
        this.bottom();
      }

      if ("idle" === this.delay && !this.isSubmitted) {
        this.idle();
      }
    }

    always() { this.showPopup(); }
    delayed(seconds = 10) { setTimeout(() => this.showPopup(), 1000 * seconds); }
    bottom() { document.addEventListener("theme:scroll", this.showOnScrollHandler); }

    idle() {
      if (!this.checkDeviceTarget()) return;
      let idleTimer = 0;
      const resetEvents = ["mousemove", "mousedown", "click", "touchmove", "touchstart", "touchend", "keydown", "keypress"];
      const triggerEvents = ["load", "resize", "scroll"];

      const startIdle = () => {
        idleTimer = setTimeout(() => {
          idleTimer = 0;
          this.showPopup();
        }, 60000);

        resetEvents.forEach(event => document.addEventListener(event, resetIdle));
        triggerEvents.forEach(event => window.addEventListener(event, resetIdle));
      };

      const resetIdle = () => {
        if (idleTimer) clearTimeout(idleTimer);
        resetEvents.forEach(event => document.removeEventListener(event, resetIdle));
        triggerEvents.forEach(event => window.removeEventListener(event, resetIdle));
        startIdle();
      };

      startIdle();
    }

    showOnScroll() {
      if (window.scrollY + window.innerHeight >= document.body.clientHeight) {
        this.showPopup();
        document.removeEventListener("theme:scroll", this.showOnScrollHandler);
      }
    }

    showPopup() {
      window.theme.closeAllTooltips();
      const popupData = { id: this.popup.id, body: this.popupBody };
      activePopups.push(popupData);

      if (this.checkDeviceTarget()) {
        // ===== NEW: Frequency Check =====
        const blockId = this.popupContainer.dataset.blockId;
        if (blockId && window.popupFrequencyManager && !window.popupFrequencyManager.shouldShow(blockId)) {
          return; // Don't show if closed in last 24 hours
        }

        openPopupsCount += 1;
        this.popup.classList.add("popup--visible");
        if (this.popup.classList.contains("popup--bar")) {
          document.body.classList.add("popup-bar-is-visible");
        }
        this.a11y.trapFocus({ container: this.popupBody });

        if (this.popup.hasAttribute("data-prevent-scroll-lock")) return;
        this.scrollLock();
      }
    }

    checkDeviceTarget() {
      const isMobileOnly = this.popup.parentNode.classList.contains("mobile");
      const isDesktopOnly = this.popup.parentNode.classList.contains("desktop");
      return !(isMobileOnly && window.innerWidth >= theme.sizes.small || isDesktopOnly && window.innerWidth < theme.sizes.small);
    }

    scrollLock() {
      document.dispatchEvent(new CustomEvent("theme:scroll:lock", { bubbles: true, detail: this.popupBody }));
    }

    unload() {
      document.removeEventListener("theme:scroll", this.showOnScrollHandler);
    }
  }

  class ReferrerTarget {
    constructor(container) {
      if (!container.hasAttribute("data-target-referrer")) return false;
      this.container = container;
      this.referrerPath = container.getAttribute("data-target-referrer");
      this.currentPath = location.href;

      if (-1 === this.currentPath.indexOf(this.referrerPath) && !window.Shopify.designMode) {
        this.container.parentNode.removeChild(this.container);
      }
    }
  }

  // ===== Large Promo Popup =====
  customElements.define("popup-large-promo", class extends HTMLElement {
    constructor() {
      super();
      this.popupContainer = this;
      this.blockId = this.dataset.blockId; // Important: from data-block-id in liquid
      this.referrerTarget = new ReferrerTarget(this);
      this.a11y = window.theme.a11y;
    }

    connectedCallback() {
      this.popup = this.querySelector("[data-popup-inner]");
      this.popupBody = this.popup.querySelector("[data-popup-body]");
      this.popupId = this.popup.id;
      this.closeBtn = this.popup.querySelector("[data-popup-close]");
      this.underlay = this.popup.querySelector("[data-popup-underlay]");
      this.form = this.popup.querySelector("[data-newsletter-form]");

      // ===== NEW: Only show if not closed in last 24 hours =====
      if (this.blockId && window.popupFrequencyManager && !window.popupFrequencyManager.shouldShow(this.blockId) && !window.Shopify.designMode) {
        return; // Do nothing
      }

      if (window.Shopify.designMode) {
        this.showPopup();
      } else {
        this.trigger = new PopupTrigger(this, this.popup);
      }

      this.initClosers();

      if (this.form) {
        setTimeout(() => {
          if (this.form.classList.contains("has-success")) {
            this.showPopup();
            openPopupsCount -= 1;
          }
        });
      }
    }

    initClosers() {
      this.closeBtn.addEventListener("click", this.closePopup.bind(this));
      this.underlay.addEventListener("click", this.closePopup.bind(this));
      this.addEventListener("keyup", (e) => {
        if (e.code === theme.keyboardKeys.ESCAPE) this.closePopup(e);
      });
    }

    closePopup(e) {
      e?.preventDefault();
      this.hidePopup();

      // ===== NEW: Mark as closed for 24 hours =====
      if (this.blockId && window.popupFrequencyManager) {
        window.popupFrequencyManager.markAsClosed(this.blockId);
      }
    }

    showPopup() {
      const isVisible = this.checkDeviceTarget();
      const popupData = { id: this.popupId, body: this.popup };
      activePopups.push(popupData);

      if (isVisible) {
        openPopupsCount += 1;
        this.popup.classList.add("popup--visible");
        this.scrollLock();
      }
    }

    hidePopup() {
      this.popup.classList.remove("popup--visible");
      const index = activePopups.findIndex(p => p.id === this.popupId);
      if (index !== -1) activePopups.splice(index, 1);

      openPopupsCount -= 1;

      if (openPopupsCount <= 0) {
        this.scrollUnlock();
        this.a11y.removeTrapFocus();
      } else if (activePopups.length > 0) {
        const lastPopup = activePopups[activePopups.length - 1].body;
        this.a11y.trapFocus({ container: lastPopup });
      }
    }

    scrollLock() {
      this.resetScrollUnlock();
      this.a11y.trapFocus({ container: this.popupBody });
      document.dispatchEvent(new CustomEvent("theme:scroll:lock", { bubbles: true, detail: this.popupBody }));
    }

    scrollUnlock() {
      this.resetScrollUnlock();
      scrollUnlockTimeout = setTimeout(() => {
        document.dispatchEvent(new CustomEvent("theme:scroll:unlock", { bubbles: true }));
      }, 300);
    }

    resetScrollUnlock() {
      if (scrollUnlockTimeout) clearTimeout(scrollUnlockTimeout);
    }

    checkDeviceTarget() {
      const mobileOnly = this.classList.contains("mobile");
      const desktopOnly = this.classList.contains("desktop");
      return !(mobileOnly && window.innerWidth >= theme.sizes.small || desktopOnly && window.innerWidth < theme.sizes.small);
    }

    disconnectedCallback() {
      this.scrollUnlock();
      if (this.trigger) this.trigger.unload();
    }
  });

  // ===== Bar Popup =====
  customElements.define("popup-bar", class extends HTMLElement {
    constructor() {
      super();
      this.popupContainer = this;
      this.blockId = this.dataset.blockId;
      this.a11y = window.theme.a11y;
    }

    connectedCallback() {
      this.popup = this.querySelector("[data-popup-inner]");
      this.popupBody = this.popup.querySelector("[data-popup-body]");
      this.popupId = this.popup.id;
      this.closeBtn = this.popup.querySelector("[data-popup-close]");
      this.underlay = this.popup.querySelector("[data-popup-underlay]");
      this.toggle = this.popup.querySelector("[data-popup-bar-toggle]");
      this.form = this.popup.querySelector("[data-newsletter-form]");

      // ===== NEW: Frequency check =====
      if (this.blockId && window.popupFrequencyManager && !window.popupFrequencyManager.shouldShow(this.blockId) && !window.Shopify.designMode) {
        return;
      }

      if (window.Shopify.designMode) {
        this.showPopup();
      } else {
        this.trigger = new PopupTrigger(this, this.popup);
      }

      this.initToggle();
      this.initClosers();

      if (this.form) {
        setTimeout(() => {
          if (this.form.classList.contains("has-success")) {
            this.showPopup();
            this.toggle.dispatchEvent(new Event("click"));
          }
          if (this.form.classList.contains("has-error")) {
            this.toggle.dispatchEvent(new Event("click"));
          }
        });
      }
    }

    initToggle() {
      this.toggle.addEventListener("click", (e) => {
        e.preventDefault();
        this.popup.classList.toggle("popup--expanded");
        if (this.popup.classList.contains("popup--expanded")) {
          this.scrollLock();
        } else {
          this.scrollUnlock();
        }
      });
    }

    initClosers() {
      this.closeBtn.addEventListener("click", this.closePopup.bind(this));
      this.underlay.addEventListener("click", () => this.toggle.dispatchEvent(new Event("click")));
      this.addEventListener("keyup", (e) => {
        if (e.code === theme.keyboardKeys.ESCAPE) {
          this.popup.classList.remove("popup--expanded");
          this.scrollUnlock();
        }
      });
    }

    closePopup(e) {
      e?.preventDefault();
      this.hidePopup();

      // ===== NEW: Save close time =====
      if (this.blockId && window.popupFrequencyManager) {
        window.popupFrequencyManager.markAsClosed(this.blockId);
      }
    }

    showPopup() {
      const popupData = { id: this.popupId, body: this.popup };
      activePopups.push(popupData);
      this.a11y.trapFocus({ container: this.popupBody });

      if (this.checkDeviceTarget()) {
        openPopupsCount += 1;
        document.body.classList.add("popup-bar-is-visible");
        this.popup.classList.add("popup--visible");
      }
    }

    hidePopup() {
      this.popup.classList.remove("popup--visible");
      document.body.classList.remove("popup-bar-is-visible");

      const index = activePopups.findIndex(p => p.id === this.popupId);
      if (index !== -1) activePopups.splice(index, 1);

      if (openPopupsCount >= 1) openPopupsCount -= 1;
      if (openPopupsCount <= 0) {
        this.scrollUnlock();
        this.a11y.removeTrapFocus();
      } else if (activePopups.length > 0) {
        const last = activePopups[activePopups.length - 1].body;
        this.a11y.trapFocus({ container: last });
      }
    }

    scrollLock() {
      document.dispatchEvent(new CustomEvent("theme:scroll:lock", { bubbles: true, detail: this.popupBody }));
    }

    scrollUnlock() {
      this.resetScrollUnlock();
      scrollUnlockTimeout = setTimeout(() => {
        document.dispatchEvent(new CustomEvent("theme:scroll:unlock", { bubbles: true }));
      }, 300);
    }

    resetScrollUnlock() {
      if (scrollUnlockTimeout) clearTimeout(scrollUnlockTimeout);
    }

    checkDeviceTarget() {
      const mobileOnly = this.classList.contains("mobile");
      const desktopOnly = this.classList.contains("desktop");
      return !(mobileOnly && window.innerWidth >= theme.sizes.small || desktopOnly && window.innerWidth < theme.sizes.small);
    }

    disconnectedCallback() {
      this.scrollUnlock();
      if (this.trigger) this.trigger.unload();
    }
  });
}();